import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { Chip } from '@grnhse/seedling/lib/birch/components/chips';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import CheckIcon from '@grnhse/seedling/lib/birch/icons/check';
import DefinitelyNotIcon from '@grnhse/seedling/lib/birch/icons/definitely_not';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { withLocalePrefix } from 'shared/utils/translation';
import type { MyGreenhouseCandidate } from 'talent_matching/my_greenhouse/hooks/use_my_greenhouse_candidates';

const t = withLocalePrefix('talent_matching.panel.work_preferences');

type Props = {
  candidate: MyGreenhouseCandidate | null;
};

const ChipSection = ({ label, items }: { label: string; items?: string[] }) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <>
      <Stack direction="column" spacing={1}>
        <SecondaryBody medium>{label}</SecondaryBody>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
          {items.map((item) => (
            <Chip key={item} text={item} />
          ))}
        </Stack>
      </Stack>
      <Divider />
    </>
  );
};

const WorkPreferencesTabPanel = ({ candidate }: Props) => {
  if (!candidate?.workPreferences) {
    return null;
  }

  const { languages, employmentTypes, worksites, location, willingToRelocate } =
    candidate.workPreferences;

  const translatedEmploymentTypes = employmentTypes?.map(
    (type) => t(`employment_type_values.${type}`) || type
  );
  const translatedWorksites = worksites?.map(
    (ws) => t(`worksite_values.${ws}`) || ws
  );

  return (
    <Stack direction="column" spacing={2}>
      <Body medium>{t('title')}</Body>

      <ChipSection label={t('languages_spoken')} items={languages} />

      <ChipSection
        label={t('employment_types')}
        items={translatedEmploymentTypes}
      />

      <ChipSection label={t('worksites')} items={translatedWorksites} />

      {location && (
        <>
          <Stack direction="column" spacing={1}>
            <SecondaryBody medium>{t('location')}</SecondaryBody>
            <SecondaryBody className="pii-blur">{location}</SecondaryBody>
          </Stack>
          <Divider />
        </>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        {willingToRelocate ? (
          <>
            <CheckIcon size="sm" color={FullPalette.GREEN_700} />
            <SecondaryBody>{t('willing_to_relocate')}</SecondaryBody>
          </>
        ) : (
          <>
            <DefinitelyNotIcon size="sm" color={FullPalette.POPPY_600} />
            <SecondaryBody>{t('not_willing_to_relocate')}</SecondaryBody>
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default WorkPreferencesTabPanel;
